import { useMemo, useState } from "react";
import { type AccessItem, accessItemsFor } from "./accessSelection.ts";

/**
 * What a provider can offer on the "Choose what Okthx can use" screen, and
 * which of it the user left on. Everything starts on.
 *
 * Holds what the user turned OFF rather than what is on, so everything
 * starting on falls out of `offered` minus that set and cannot drift from what
 * the provider actually offers when the provider changes.
 */
export function useAccessChoice(provider: string): {
  offered: readonly AccessItem[];
  selected: string[];
  toggle: (id: string) => void;
} {
  const offered = useMemo(() => accessItemsFor(provider), [provider]);
  const [deselected, setDeselected] = useState<ReadonlySet<string>>(() => new Set());
  const selected = useMemo(
    () => offered.map((item) => item.id).filter((id) => !deselected.has(id)),
    [offered, deselected],
  );
  const toggle = (id: string) =>
    setDeselected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return { offered, selected, toggle };
}
